import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type { NoticeProof } from './notice-fidelity.models';
import {
  computeNoticeProofScale,
  NoticeProofViewportDirective,
  type NoticeProofViewport,
  type NoticeProofZoom,
} from './notice-proof-viewport';

/** Navigation of a captured proof; source and evidence authority stay with the owner. */
@Component({
  selector: 'phms-notice-proof-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzStandardStateComponent,
    NoticeProofViewportDirective,
  ],
  templateUrl: './notice-proof-viewer.component.html',
  styleUrl: './notice-proof-viewer.component.scss',
})
export class NoticeProofViewerComponent implements OnChanges, AfterViewChecked {
  @Input() proof: NoticeProof | null = null;
  @Input() disabled = false;
  @Output() readonly downloadPdf = new EventEmitter<void>();
  @Output() readonly checkWording = new EventEmitter<void>();
  @ViewChild('proofCanvas') private proofCanvas?: ElementRef<HTMLElement>;

  pageNumber = 1;
  zoom: NoticeProofZoom = 'fit-page';
  viewport: NoticeProofViewport | null = null;
  private proofIdentity: string | null = null;
  private resetCanvasScroll = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['proof']) return;
    const proof = this.displayedProof;
    const identity = proof
      ? JSON.stringify([
          proof.sourceJson,
          proof.sourceHash,
          proof.exampleCode,
          proof.contextHash,
          proof.rendererCode,
          proof.rendererVersion,
        ])
      : null;
    if (identity !== this.proofIdentity) {
      this.pageNumber = 1;
      this.zoom = 'fit-page';
      this.resetCanvasScroll = true;
    }
    this.proofIdentity = identity;
    const page = Math.min(Math.max(1, this.pageNumber), this.pageCount || 1);
    if (page !== this.pageNumber) this.resetCanvasScroll = true;
    this.pageNumber = page;
    if (!proof) this.viewport = null;
  }

  ngAfterViewChecked(): void {
    if (!this.resetCanvasScroll || !this.proofCanvas) return;
    this.resetCanvasScroll = false;
    this.proofCanvas.nativeElement.scrollTop = 0;
    this.proofCanvas.nativeElement.scrollLeft = 0;
  }

  /** Only checks that the already-authorized proof can be paged safely. */
  get displayedProof(): NoticeProof | null {
    const proof = this.proof;
    if (
      !proof ||
      !Number.isInteger(proof.pageCount) ||
      proof.pageCount < 1 ||
      proof.pages.length !== proof.pageCount ||
      !proof.pages.every(
        (page, index) =>
          page.number === index + 1 &&
          Number.isFinite(page.widthPx) &&
          page.widthPx > 0 &&
          Number.isFinite(page.heightPx) &&
          page.heightPx > 0 &&
          !!page.pngBase64,
      )
    )
      return null;
    return proof;
  }

  get pageCount(): number {
    return this.displayedProof?.pageCount ?? 0;
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index + 1);
  }

  get selectedPage(): NoticeProof['pages'][number] | null {
    return this.displayedProof?.pages[this.pageNumber - 1] ?? null;
  }

  get scale(): number | null {
    const page = this.selectedPage;
    return page
      ? computeNoticeProofScale(this.zoom, [
          { ...page, viewport: this.viewport },
        ])
      : null;
  }

  get actualZoom(): number | null {
    const scale = this.scale;
    return scale === null ? null : Math.round(scale * 100);
  }

  setPage(value: number): void {
    const page = Number(value);
    if (
      !this.disabled &&
      Number.isInteger(page) &&
      page >= 1 &&
      page <= this.pageCount &&
      page !== this.pageNumber
    ) {
      this.pageNumber = page;
      this.resetCanvasScroll = true;
    }
  }

  setZoom(value: NoticeProofZoom | number): void {
    if (
      !this.disabled &&
      this.displayedProof &&
      (value === 'fit-page' ||
        value === 'fit-width' ||
        value === 50 ||
        value === 75 ||
        value === 100)
    )
      this.zoom = value;
  }

  pageKeydown(event: KeyboardEvent): void {
    if (
      this.disabled ||
      event.target !== event.currentTarget ||
      !this.displayedProof
    )
      return;
    const requested =
      event.key === 'ArrowLeft'
        ? this.pageNumber - 1
        : event.key === 'ArrowRight'
          ? this.pageNumber + 1
          : event.key === 'Home'
            ? 1
            : event.key === 'End'
              ? this.pageCount
              : null;
    if (requested === null) return;
    event.preventDefault();
    this.setPage(Math.min(Math.max(1, requested), this.pageCount));
  }

  download(): void {
    if (!this.disabled && this.displayedProof) this.downloadPdf.emit();
  }
}
